import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter as Router, withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import store from "./store";
import { setIsOutsage } from "./actions/globalActions";
import { setIsNetwork } from "./actions/globalNetwork";
import { logErrors } from "./components/log-errors/logErrors";
import UserApi from "./v2/services/api/user";

import Outage from "./pages/Outage";
import NetworkError from "./pages/NetworkError";
import AppContext from "./v2/context";

import { extractAnalyticsCookies, serializeCookie } from "./v2/utils/extractor";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import Constants from "./v2/config/constant";
import LocaleHandler from "./v2/utils/locale-handler";
import { initializedGenericOptions } from "./v2/features/generic-data";
import {
  getAuthUserAccount,
  getAuthUserProfile,
  initializeApp,
} from "./v2/features/application";
import Config from "./v2/config";
import NotificationBar from "./v2/pages/create-flow/templates/notification-bar";
import isEqual from "lodash/isEqual";
import isEmpty from "lodash/isEmpty";
import Navbar from "./components/common/Navbar";
import { useIsMobileScreen } from "./v2/hooks";
import AppRoutes from "./v2/routes";
import * as Comlink from "comlink";
import AuthRevalidateWorker from "./v2/webworkers/revalidateAuth.worker";

const AppContainer = withRouter(({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { snackNotification } = useSelector((state) => state.application);
  const { initialized, authenticated } = useSelector(
    (state) => state.application
  );
  const previouseLocationRef = useRef({});

  const { isOutage } = useSelector((state) => state.globalReducer);
  const { isNetwork } = useSelector((state) => state.globalNetwork);
  const [appLanguage, setAppLanguage] = useState(
    LocaleHandler.getLanguageFromStorage()
  );
  const [lng, handleLng] = useState(
    LocaleHandler.getLanguageFromStorage() === Constants.APP_LANG.EN
  );
  const isMobileScreen = useIsMobileScreen();

  useEffect(() => {
    const blockListener = registerBlockRouteListener();
    return () => {
      blockListener();
    };
  });

  useEffect(() => {
    if (!initialized) {
      dispatch(initializeApp());
    }
  }, [dispatch, initialized]);

  useEffect(() => {
    const previousLocation = previouseLocationRef.current;
    if (isEqual(location, previousLocation)) {
      return;
    }
    previouseLocationRef.current = location;
    persistUtmTagsToCookie();
  }, [dispatch, location]);

  useEffect(() => {
    LocaleHandler.changeLanguage(appLanguage);
    localStorage.setItem("lng", appLanguage === Constants.APP_LANG.EN);
    dispatch(initializedGenericOptions(appLanguage));
  }, [appLanguage, dispatch]);

  useEffect(() => {
    if (authenticated && !isEmpty(document.cookie)) {
      const analyticsCookies = extractAnalyticsCookies();
      UserApi.postUserAnalyticsCookies({ cookies: analyticsCookies });
    }
  }, [authenticated]);

  // Refer this function as a function in utility layer
  const persistUtmTagsToCookie = () => {
    const location = window.location;
    if (isEmpty(location.search)) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const utmTags = {};
    for (const tag of Constants.UTM_TAG_KEYS) {
      const value = searchParams.get(tag);
      if (value !== null) {
        utmTags[tag] = value;
      }
    }
    if (Object.entries(utmTags).length > 0) {
      if (utmTags.utm_campaign !== undefined) {
        document.cookie = serializeCookie("utm_campaign", utmTags.utm_campaign);
      }
      if (utmTags.utm_medium !== undefined) {
        document.cookie = serializeCookie("utm_medium", utmTags.utm_medium);
      }
      if (utmTags.utm_source !== undefined) {
        document.cookie = serializeCookie("utm_source", utmTags.utm_source);
      }
    }
  };

  const registerBlockRouteListener = () => {
    const listener = history.block((location, _action) => {
      const previousLocation = previouseLocationRef.current;
      if (isEmpty(previousLocation)) {
        return;
      }
      // Block same route navigation
      if (isEqual(location.pathname, previousLocation.pathname)) {
        // Check same route query params
        const shouldBlock = !isEqual(location.search, previousLocation.search);
        return shouldBlock;
      }
      return true;
    });
    return listener;
  };

  return (
    <AppContext.Provider
      value={{
        monthlyMagazineVisibility: Config.MAGAZINE_VISIBILITY,
        appLanguage,
        setAppLanguage,
        lng,
        handleLng,
      }}
    >
      <Navbar lng={lng} handleLng={handleLng} isMobileScreen={isMobileScreen} />
      {isNetwork ? <NetworkError /> : isOutage ? <Outage /> : children}
      <NotificationBar
        severity={snackNotification.severity}
        message={snackNotification.message}
      />
    </AppContext.Provider>
  );
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.message === "Network Error") {
      store.dispatch(setIsNetwork(true));
    }

    if (
      error.response.status === 501 ||
      error.response.status === 502 ||
      error.response.status === 503 ||
      error.response.status === 520
    ) {
      store.dispatch(setIsOutsage(true));
    }

    if (error && error.response) {
      if (error.response.status !== 401) {
        if (error.response.status !== 410) {
          logErrors(error.response);
        }
      }
    }
    if (
      error.response &&
      error.response.status === 401 &&
      localStorage.getItem("auth_token")
    ) {
      logout();
    }
    return Promise.reject(error);
  }
);

const logout = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("userId");
  localStorage.removeItem("post_details");
  localStorage.removeItem("postId");
  localStorage.removeItem("session_creation");
  localStorage.removeItem("personalInfo");
  localStorage.removeItem("parentInfo");
  localStorage.removeItem("privateInfo");
  localStorage.removeItem("user_name");
  localStorage.removeItem("user_email");
  sessionStorage.removeItem("chat_token");
  sessionStorage.removeItem("chat_token_expires_in");
  localStorage.removeItem("user_role");
  localStorage.removeItem("operator_id");

  // Refactor this with react router dom history
  window.location.href = "/login";
};

function App(props) {
  const dispatch = useDispatch();
  const authRevalidatorRef = useRef(null);

  const registerAuthRevalidator = useCallback(async () => {
    setInterval(async () => {
      try {
        const authToken = localStorage.getItem("auth_token");
        console.log(">>>> authToken ", authToken);
        if (isEmpty(authToken)) {
          if (authRevalidatorRef.current !== null) {
            authRevalidatorRef.current = null;
          }
          return;
        }
        const RevalidatorImpl = Comlink.wrap(new AuthRevalidateWorker());
        if (authRevalidatorRef.current === null) {
          const instance = await new RevalidatorImpl(authToken);
          authRevalidatorRef.current = instance;
        }
        const authRevalidator = authRevalidatorRef.current;
        authRevalidator.validate().then((result) => {
          if (result.updated) {
            dispatch(getAuthUserProfile());
            dispatch(getAuthUserAccount());
          }
        });
      } catch (error) {}
    }, Config.AUTH_REVALIDATION_INTERVAL * 1000);
  }, [dispatch]);

  useEffect(() => {
    registerAuthRevalidator();
  });

  return (
    <Router>
      <AppContainer>
        <AppRoutes />
      </AppContainer>
    </Router>
  );
}

export default withRouter(App);
